import React, { useState } from 'react';
import { useSEO } from '../hooks/useSEO';

const ScholarshipsPage: React.FC = () => {
  useSEO();

  const [formData, setFormData] = useState({
    fullName: '',
    email: '',
    phone: '',
    course: '',
    scholarshipType: '',
    academicPercentage: '',
    familyIncome: '',
    category: '',
    specialAchievements: '',
    statementOfPurpose: ''
  });

  const [flippedCards, setFlippedCards] = useState<Record<string, boolean>>({});

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleCardFlip = (cardId: string) => {
    setFlippedCards(prev => ({
      ...prev,
      [cardId]: !prev[cardId]
    }));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    console.log('Scholarship application submitted:', formData);
    // Handle form submission here
    alert('Scholarship application submitted successfully!');
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100">
      {/* Hero Section */}
      <div className="bg-gradient-to-r from-blue-600 to-purple-700 text-white py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-4xl md:text-5xl font-bold mb-6">
            SEWA Scholarship Programs
          </h1>
          <p className="text-xl md:text-2xl mb-8 text-blue-100">
            Empowering Dreams Through Education Financial Support
          </p>
          <div className="bg-white/10 backdrop-blur-sm rounded-lg p-6 max-w-2xl mx-auto">
            <p className="text-lg">
              At SEWA, we believe every deserving student should have access to quality education. 
              Our comprehensive scholarship programs support students from various backgrounds and academic achievements.
            </p>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        {/* Scholarship Programs Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-2 gap-8 mb-16">
          {/* Merit Scholarship */}
          <div className="relative h-96 perspective-1000">
            <div 
              className={`relative w-full h-full transition-transform duration-700 transform-style-preserve-3d cursor-pointer ${
                flippedCards['merit'] ? 'rotate-y-180' : ''
              }`}
              onClick={() => handleCardFlip('merit')}
            >
              {/* Front of card */}
              <div className="absolute inset-0 w-full h-full backface-hidden bg-white rounded-lg shadow-lg p-8 hover:shadow-xl transition-shadow">
                <div className="text-center mb-6">
                  <div className="w-20 h-20 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <span className="text-3xl">🏆</span>
                  </div>
                  <h3 className="text-2xl font-bold text-gray-900">Merit Scholarship</h3>
                  <p className="text-base text-blue-600 mt-3">Click to see eligibility criteria</p>
                </div>
                <div className="space-y-4 text-gray-700">
                  <p className="text-lg"><strong>Coverage:</strong> Up to 50% of tuition fees</p>
                  <p className="text-lg"><strong>Duration:</strong> Throughout the course</p>
                  <p className="text-lg"><strong>Benefits:</strong> Academic excellence recognition</p>
                </div>
              </div>
              
              {/* Back of card */}
              <div className="absolute inset-0 w-full h-full backface-hidden rotate-y-180 bg-gradient-to-br from-blue-500 to-blue-700 rounded-lg shadow-lg p-8 text-white">
                <div className="text-center mb-6">
                  <h3 className="text-2xl font-bold mb-2">Merit Scholarship</h3>
                  <h4 className="text-xl font-semibold text-blue-100">Eligibility Criteria</h4>
                </div>
                <div className="space-y-4 text-base">
                  <div className="flex items-start">
                    <span className="text-blue-200 mr-3 mt-1 text-lg">✓</span>
                    <span>85%+ in 12th standard</span>
                  </div>
                  <div className="flex items-start">
                    <span className="text-blue-200 mr-3 mt-1 text-lg">✓</span>
                    <span>Consistent academic performance</span>
                  </div>
                  <div className="flex items-start">
                    <span className="text-blue-200 mr-3 mt-1 text-lg">✓</span>
                    <span>No backlogs in previous studies</span>
                  </div>
                  <div className="flex items-start">
                    <span className="text-blue-200 mr-3 mt-1 text-lg">✓</span>
                    <span>Valid entrance exam scores</span>
                  </div>
                  <div className="mt-6 pt-3 border-t border-blue-400">
                    <p className="text-sm text-blue-100 text-center">Click again to flip back</p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Need-based Scholarship */}
          <div className="relative h-96 perspective-1000">
            <div 
              className={`relative w-full h-full transition-transform duration-700 transform-style-preserve-3d cursor-pointer ${
                flippedCards['need-based'] ? 'rotate-y-180' : ''
              }`}
              onClick={() => handleCardFlip('need-based')}
            >
              {/* Front of card */}
              <div className="absolute inset-0 w-full h-full backface-hidden bg-white rounded-lg shadow-lg p-8 hover:shadow-xl transition-shadow">
                <div className="text-center mb-6">
                  <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <span className="text-3xl">🤝</span>
                  </div>
                  <h3 className="text-2xl font-bold text-gray-900">Need-based Scholarship</h3>
                  <p className="text-base text-green-600 mt-3">Click to see eligibility criteria</p>
                </div>
                <div className="space-y-4 text-gray-700">
                  <p className="text-lg"><strong>Coverage:</strong> Up to 70% of total fees</p>
                  <p className="text-lg"><strong>Duration:</strong> Subject to annual review</p>
                  <p className="text-lg"><strong>Benefits:</strong> Financial support for deserving students</p>
                </div>
              </div>
              
              {/* Back of card */}
              <div className="absolute inset-0 w-full h-full backface-hidden rotate-y-180 bg-gradient-to-br from-green-500 to-green-700 rounded-lg shadow-lg p-8 text-white">
                <div className="text-center mb-6">
                  <h3 className="text-2xl font-bold mb-2">Need-based Scholarship</h3>
                  <h4 className="text-xl font-semibold text-green-100">Eligibility Criteria</h4>
                </div>
                <div className="space-y-4 text-base">
                  <div className="flex items-start">
                    <span className="text-green-200 mr-3 mt-1 text-lg">✓</span>
                    <span>Family income below ₹3 lakhs annually</span>
                  </div>
                  <div className="flex items-start">
                    <span className="text-green-200 mr-3 mt-1 text-lg">✓</span>
                    <span>Income certificate required</span>
                  </div>
                  <div className="flex items-start">
                    <span className="text-green-200 mr-3 mt-1 text-lg">✓</span>
                    <span>Minimum 70% in 12th standard</span>
                  </div>
                  <div className="flex items-start">
                    <span className="text-green-200 mr-3 mt-1 text-lg">✓</span>
                    <span>Bank statements for verification</span>
                  </div>
                  <div className="mt-6 pt-3 border-t border-green-400">
                    <p className="text-sm text-green-100 text-center">Click again to flip back</p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Sports Scholarship */}
          <div className="relative h-96 perspective-1000">
            <div 
              className={`relative w-full h-full transition-transform duration-700 transform-style-preserve-3d cursor-pointer ${
                flippedCards['sports'] ? 'rotate-y-180' : ''
              }`}
              onClick={() => handleCardFlip('sports')}
            >
              {/* Front of card */}
              <div className="absolute inset-0 w-full h-full backface-hidden bg-white rounded-lg shadow-lg p-8 hover:shadow-xl transition-shadow">
                <div className="text-center mb-6">
                  <div className="w-20 h-20 bg-yellow-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <span className="text-3xl">⚽</span>
                  </div>
                  <h3 className="text-2xl font-bold text-gray-900">Sports Scholarship</h3>
                  <p className="text-base text-yellow-600 mt-3">Click to see eligibility criteria</p>
                </div>
                <div className="space-y-4 text-gray-700">
                  <p className="text-lg"><strong>Coverage:</strong> Up to 40% of tuition fees</p>
                  <p className="text-lg"><strong>Duration:</strong> Based on sports participation</p>
                  <p className="text-lg"><strong>Benefits:</strong> Sports facility access and training</p>
                </div>
              </div>
              
              {/* Back of card */}
              <div className="absolute inset-0 w-full h-full backface-hidden rotate-y-180 bg-gradient-to-br from-yellow-500 to-yellow-700 rounded-lg shadow-lg p-8 text-white">
                <div className="text-center mb-6">
                  <h3 className="text-2xl font-bold mb-2">Sports Scholarship</h3>
                  <h4 className="text-xl font-semibold text-yellow-100">Eligibility Criteria</h4>
                </div>
                <div className="space-y-4 text-base">
                  <div className="flex items-start">
                    <span className="text-yellow-200 mr-3 mt-1 text-lg">✓</span>
                    <span>State/National level achievements</span>
                  </div>
                  <div className="flex items-start">
                    <span className="text-yellow-200 mr-3 mt-1 text-lg">✓</span>
                    <span>Valid sports certificates</span>
                  </div>
                  <div className="flex items-start">
                    <span className="text-yellow-200 mr-3 mt-1 text-lg">✓</span>
                    <span>Minimum 60% in 12th standard</span>
                  </div>
                  <div className="flex items-start">
                    <span className="text-yellow-200 mr-3 mt-1 text-lg">✓</span>
                    <span>Commitment to college sports team</span>
                  </div>
                  <div className="mt-6 pt-3 border-t border-yellow-400">
                    <p className="text-sm text-yellow-100 text-center">Click again to flip back</p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Minority Scholarship */}
          <div className="relative h-96 perspective-1000">
            <div 
              className={`relative w-full h-full transition-transform duration-700 transform-style-preserve-3d cursor-pointer ${
                flippedCards['minority'] ? 'rotate-y-180' : ''
              }`}
              onClick={() => handleCardFlip('minority')}
            >
              {/* Front of card */}
              <div className="absolute inset-0 w-full h-full backface-hidden bg-white rounded-lg shadow-lg p-8 hover:shadow-xl transition-shadow">
                <div className="text-center mb-6">
                  <div className="w-20 h-20 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <span className="text-3xl">🎓</span>
                  </div>
                  <h3 className="text-2xl font-bold text-gray-900">Minority Scholarship</h3>
                  <p className="text-base text-purple-600 mt-3">Click to see eligibility criteria</p>
                </div>
                <div className="space-y-4 text-gray-700">
                  <p className="text-lg"><strong>Coverage:</strong> Up to 60% of fees</p>
                  <p className="text-lg"><strong>Duration:</strong> Throughout the course</p>
                  <p className="text-lg"><strong>Benefits:</strong> Inclusive education support</p>
                </div>
              </div>
              
              {/* Back of card */}
              <div className="absolute inset-0 w-full h-full backface-hidden rotate-y-180 bg-gradient-to-br from-purple-500 to-purple-700 rounded-lg shadow-lg p-8 text-white">
                <div className="text-center mb-6">
                  <h3 className="text-2xl font-bold mb-2">Minority Scholarship</h3>
                  <h4 className="text-xl font-semibold text-purple-100">Eligibility Criteria</h4>
                </div>
                <div className="space-y-4 text-base">
                  <div className="flex items-start">
                    <span className="text-purple-200 mr-3 mt-1 text-lg">✓</span>
                    <span>Belong to minority community</span>
                  </div>
                  <div className="flex items-start">
                    <span className="text-purple-200 mr-3 mt-1 text-lg">✓</span>
                    <span>Valid community certificate</span>
                  </div>
                  <div className="flex items-start">
                    <span className="text-purple-200 mr-3 mt-1 text-lg">✓</span>
                    <span>Minimum 65% in 12th standard</span>
                  </div>
                  <div className="flex items-start">
                    <span className="text-purple-200 mr-3 mt-1 text-lg">✓</span>
                    <span>No existing scholarships above 40%</span>
                  </div>
                  <div className="mt-6 pt-3 border-t border-purple-400">
                    <p className="text-sm text-purple-100 text-center">Click again to flip back</p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Women Empowerment Scholarship */}
          <div className="relative h-96 perspective-1000">
            <div 
              className={`relative w-full h-full transition-transform duration-700 transform-style-preserve-3d cursor-pointer ${
                flippedCards['women'] ? 'rotate-y-180' : ''
              }`}
              onClick={() => handleCardFlip('women')}
            >
              {/* Front of card */}
              <div className="absolute inset-0 w-full h-full backface-hidden bg-white rounded-lg shadow-lg p-8 hover:shadow-xl transition-shadow">
                <div className="text-center mb-6">
                  <div className="w-20 h-20 bg-pink-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <span className="text-3xl">👩‍🎓</span>
                  </div>
                  <h3 className="text-2xl font-bold text-gray-900">Women Empowerment</h3>
                  <p className="text-base text-pink-600 mt-3">Click to see eligibility criteria</p>
                </div>
                <div className="space-y-4 text-gray-700">
                  <p className="text-lg"><strong>Coverage:</strong> Up to 30% of tuition fees</p>
                  <p className="text-lg"><strong>Duration:</strong> Throughout the course</p>
                  <p className="text-lg"><strong>Benefits:</strong> Mentorship and career guidance</p>
                </div>
              </div>
              
              {/* Back of card */}
              <div className="absolute inset-0 w-full h-full backface-hidden rotate-y-180 bg-gradient-to-br from-pink-500 to-pink-700 rounded-lg shadow-lg p-8 text-white">
                <div className="text-center mb-6">
                  <h3 className="text-2xl font-bold mb-2">Women Empowerment</h3>
                  <h4 className="text-xl font-semibold text-pink-100">Eligibility Criteria</h4>
                </div>
                <div className="space-y-4 text-base">
                  <div className="flex items-start">
                    <span className="text-pink-200 mr-3 mt-1 text-lg">✓</span>
                    <span>Female students in STEM fields</span>
                  </div>
                  <div className="flex items-start">
                    <span className="text-pink-200 mr-3 mt-1 text-lg">✓</span>
                    <span>Minimum 70% in 12th standard</span>
                  </div>
                  <div className="flex items-start">
                    <span className="text-pink-200 mr-3 mt-1 text-lg">✓</span>
                    <span>Interest in technology/sciences</span>
                  </div>
                  <div className="flex items-start">
                    <span className="text-pink-200 mr-3 mt-1 text-lg">✓</span>
                    <span>Leadership potential assessment</span>
                  </div>
                  <div className="mt-6 pt-3 border-t border-pink-400">
                    <p className="text-sm text-pink-100 text-center">Click again to flip back</p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Rural Development Scholarship */}
          <div className="relative h-96 perspective-1000">
            <div 
              className={`relative w-full h-full transition-transform duration-700 transform-style-preserve-3d cursor-pointer ${
                flippedCards['rural'] ? 'rotate-y-180' : ''
              }`}
              onClick={() => handleCardFlip('rural')}
            >
              {/* Front of card */}
              <div className="absolute inset-0 w-full h-full backface-hidden bg-white rounded-lg shadow-lg p-8 hover:shadow-xl transition-shadow">
                <div className="text-center mb-6">
                  <div className="w-20 h-20 bg-orange-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <span className="text-3xl">🌾</span>
                  </div>
                  <h3 className="text-2xl font-bold text-gray-900">Rural Development</h3>
                  <p className="text-base text-orange-600 mt-3">Click to see eligibility criteria</p>
                </div>
                <div className="space-y-4 text-gray-700">
                  <p className="text-lg"><strong>Coverage:</strong> Up to 50% of total fees</p>
                  <p className="text-lg"><strong>Duration:</strong> Throughout the course</p>
                  <p className="text-lg"><strong>Benefits:</strong> Accommodation and meal support</p>
                </div>
              </div>
              
              {/* Back of card */}
              <div className="absolute inset-0 w-full h-full backface-hidden rotate-y-180 bg-gradient-to-br from-orange-500 to-orange-700 rounded-lg shadow-lg p-8 text-white">
                <div className="text-center mb-6">
                  <h3 className="text-2xl font-bold mb-2">Rural Development</h3>
                  <h4 className="text-xl font-semibold text-orange-100">Eligibility Criteria</h4>
                </div>
                <div className="space-y-4 text-base">
                  <div className="flex items-start">
                    <span className="text-orange-200 mr-3 mt-1 text-lg">✓</span>
                    <span>Students from rural areas</span>
                  </div>
                  <div className="flex items-start">
                    <span className="text-orange-200 mr-3 mt-1 text-lg">✓</span>
                    <span>Valid domicile certificate</span>
                  </div>
                  <div className="flex items-start">
                    <span className="text-orange-200 mr-3 mt-1 text-lg">✓</span>
                    <span>Minimum 65% in 12th standard</span>
                  </div>
                  <div className="flex items-start">
                    <span className="text-orange-200 mr-3 mt-1 text-lg">✓</span>
                    <span>Commitment to rural development</span>
                  </div>
                  <div className="mt-6 pt-3 border-t border-orange-400">
                    <p className="text-sm text-orange-100 text-center">Click again to flip back</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Application Process */}
        <div className="bg-white rounded-lg shadow-lg p-8 mb-16">
          <h2 className="text-3xl font-bold text-center text-gray-900 mb-8">Application Process</h2>
          <div className="grid md:grid-cols-4 gap-6">
            <div className="text-center">
              <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-2xl font-bold text-blue-600">1</span>
              </div>
              <h3 className="font-semibold text-gray-900 mb-2">Complete Application</h3>
              <p className="text-gray-600">Fill out the comprehensive scholarship application form below</p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-2xl font-bold text-green-600">2</span>
              </div>
              <h3 className="font-semibold text-gray-900 mb-2">Document Verification</h3>
              <p className="text-gray-600">Submit required academic and financial documents</p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 bg-yellow-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-2xl font-bold text-yellow-600">3</span>
              </div>
              <h3 className="font-semibold text-gray-900 mb-2">Review Process</h3>
              <p className="text-gray-600">Our committee evaluates applications based on eligibility criteria</p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-2xl font-bold text-purple-600">4</span>
              </div>
              <h3 className="font-semibold text-gray-900 mb-2">Award Notification</h3>
              <p className="text-gray-600">Selected candidates are notified within 15-20 working days</p>
            </div>
          </div>
        </div>

        {/* Scholarship Statistics */}
        <div className="bg-gradient-to-r from-blue-600 to-purple-700 rounded-lg shadow-lg p-8 mb-16 text-white">
          <h2 className="text-3xl font-bold text-center mb-8">Our Impact</h2>
          <div className="grid md:grid-cols-4 gap-6 text-center">
            <div>
              <div className="text-4xl font-bold mb-2">500+</div>
              <div className="text-blue-100">Students Benefited</div>
            </div>
            <div>
              <div className="text-4xl font-bold mb-2">₹2.5Cr</div>
              <div className="text-blue-100">Total Scholarships</div>
            </div>
            <div>
              <div className="text-4xl font-bold mb-2">85%</div>
              <div className="text-blue-100">Success Rate</div>
            </div>
            <div>
              <div className="text-4xl font-bold mb-2">6</div>
              <div className="text-blue-100">Scholarship Types</div>
            </div>
          </div>
        </div>

        {/* Single Comprehensive Application Form */}
        <div className="bg-white rounded-lg shadow-lg p-8">
          <h2 className="text-3xl font-bold text-center text-gray-900 mb-8">
            Scholarship Application Form
          </h2>
          <p className="text-center text-gray-600 mb-8">
            Complete this form to apply for any of our scholarship programs. Our team will review your application and contact you with further details.
          </p>

          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Personal Information */}
            <div className="bg-gray-50 rounded-lg p-6">
              <h3 className="text-xl font-semibold text-gray-900 mb-4">Personal Information</h3>
              <div className="grid md:grid-cols-2 gap-4">
                <input
                  type="text"
                  name="fullName"
                  placeholder="Full Name *"
                  value={formData.fullName}
                  onChange={handleInputChange}
                  required
                  className="w-full px-4 py-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
                <input
                  type="email"
                  name="email"
                  placeholder="Email Address *"
                  value={formData.email}
                  onChange={handleInputChange}
                  required
                  className="w-full px-4 py-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
                <input
                  type="tel"
                  name="phone"
                  placeholder="Phone Number *"
                  value={formData.phone}
                  onChange={handleInputChange}
                  required
                  className="w-full px-4 py-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
                <select
                  name="course"
                  value={formData.course}
                  onChange={handleInputChange}
                  required
                  className="w-full px-4 py-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="">Select Course *</option>
                  <option value="MBBS">MBBS</option>
                  <option value="BDS">BDS</option>
                  <option value="B.Sc Nursing">B.Sc Nursing</option>
                  <option value="B.Pharm">B.Pharm</option>
                  <option value="BPT">BPT</option>
                  <option value="BAMS">BAMS</option>
                  <option value="BHMS">BHMS</option>
                  <option value="Engineering">Engineering</option>
                  <option value="B.Tech CSE">B.Tech CSE</option>
                  <option value="B.E Civil">B.E Civil</option>
                  <option value="B.E Mechanical">B.E Mechanical</option>
                  <option value="B.E Electrical">B.E Electrical</option>
                  <option value="B.Sc Computer Science">B.Sc Computer Science</option>
                  <option value="BBA">BBA</option>
                  <option value="BCA">BCA</option>
                  <option value="Other">Other</option>
                </select>
              </div>
            </div>

            {/* Academic Information */}
            <div className="bg-gray-50 rounded-lg p-6">
              <h3 className="text-xl font-semibold text-gray-900 mb-4">Academic Information</h3>
              <div className="grid md:grid-cols-2 gap-4">
                <input
                  type="number"
                  name="academicPercentage"
                  placeholder="12th Standard Percentage *"
                  value={formData.academicPercentage}
                  onChange={handleInputChange}
                  required
                  min="0"
                  max="100"
                  className="w-full px-4 py-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
                <select
                  name="scholarshipType"
                  value={formData.scholarshipType}
                  onChange={handleInputChange}
                  required
                  className="w-full px-4 py-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="">Select Scholarship Type *</option>
                  <option value="Merit">Merit Scholarship</option>
                  <option value="Need-based">Need-based Scholarship</option>
                  <option value="Sports">Sports Scholarship</option>
                  <option value="Minority">Minority Scholarship</option>
                  <option value="Women Empowerment">Women Empowerment</option>
                  <option value="Rural Development">Rural Development</option>
                </select>
              </div>
            </div>

            {/* Financial Information */}
            <div className="bg-gray-50 rounded-lg p-6">
              <h3 className="text-xl font-semibold text-gray-900 mb-4">Financial Information</h3>
              <div className="grid md:grid-cols-2 gap-4">
                <input
                  type="number"
                  name="familyIncome"
                  placeholder="Annual Family Income (₹)"
                  value={formData.familyIncome}
                  onChange={handleInputChange}
                  className="w-full px-4 py-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
                <select
                  name="category"
                  value={formData.category}
                  onChange={handleInputChange}
                  className="w-full px-4 py-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="">Select Category</option>
                  <option value="General">General</option>
                  <option value="OBC">OBC</option>
                  <option value="SC">SC</option>
                  <option value="ST">ST</option>
                  <option value="Minority">Minority</option>
                </select>
              </div>
            </div>

            {/* Additional Information */}
            <div className="bg-gray-50 rounded-lg p-6">
              <h3 className="text-xl font-semibold text-gray-900 mb-4">Additional Information</h3>
              <div className="space-y-4">
                <textarea
                  name="specialAchievements"
                  placeholder="Special Achievements (Sports, Extra-curricular, Awards, etc.)"
                  value={formData.specialAchievements}
                  onChange={handleInputChange}
                  rows={3}
                  className="w-full px-4 py-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                ></textarea>
                <textarea
                  name="statementOfPurpose"
                  placeholder="Statement of Purpose - Why do you deserve this scholarship? Describe your goals and how this scholarship will help you achieve them. *"
                  value={formData.statementOfPurpose}
                  onChange={handleInputChange}
                  required
                  rows={5}
                  className="w-full px-4 py-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                ></textarea>
              </div>
            </div>

            {/* Terms and Submit */}
            <div className="bg-blue-50 rounded-lg p-6">
              <div className="flex items-start space-x-3 mb-6">
                <input
                  type="checkbox"
                  id="terms"
                  required
                  className="mt-1 h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                />
                <label htmlFor="terms" className="text-sm text-gray-700">
                  I agree to the terms and conditions and confirm that all information provided is accurate and complete. 
                  I understand that false information may result in disqualification from the scholarship program.
                </label>
              </div>
              <button
                type="submit"
                className="w-full bg-gradient-to-r from-blue-600 to-purple-700 text-white py-4 px-8 rounded-md hover:from-blue-700 hover:to-purple-800 transition-all duration-200 font-semibold text-lg shadow-lg"
              >
                Submit Scholarship Application
              </button>
            </div>
          </form>
        </div>

        {/* Contact Information */}
        <div className="mt-16 bg-gray-50 rounded-lg p-8 text-center">
          <h3 className="text-2xl font-bold text-gray-900 mb-4">Need Help with Your Application?</h3>
          <p className="text-gray-600 mb-6">
            Our scholarship counselors are here to assist you with any questions about the application process.
          </p>
          <div className="grid md:grid-cols-3 gap-6">
            <div>
              <h4 className="font-semibold text-gray-900 mb-2">📞 Phone Support</h4>
              <p className="text-blue-600">+91 98765 43210</p>
            </div>
            <div>
              <h4 className="font-semibold text-gray-900 mb-2">📧 Email Support</h4>
              <p className="text-blue-600">infonammasewa@gmail.com</p>
            </div>
            <div>
              <h4 className="font-semibold text-gray-900 mb-2">🕒 Office Hours</h4>
              <p className="text-gray-600">Mon-Sat: 9 AM - 6 PM</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ScholarshipsPage;
